import { Component, OnInit, ChangeDetectorRef, ViewChild, Output, EventEmitter } from '@angular/core';
import { MatCalendar } from '@angular/material/datepicker';
import { SettingsService } from '../settings.service';
import { FormBuilder } from '@angular/forms';
/**
 * Interface representing a weekday configuration.
 */
interface WeekDay {
  name: string;        // Name of the day (e.g. Monday)
  value: number;       // Day index (0 = Sunday, 1 = Monday, etc.)
  selected: boolean;   // Whether this day is selected (active)
  from: string;        // Start time (12-hour format)
  to: string;          // End time (12-hour format)
  status: string;      // Status for the day ('Available' or 'Busy')
}

@Component({
  selector: 'app-schedule-settings',
  templateUrl: './schedule-settings.component.html',
  styleUrls: ['./schedule-settings.component.scss']
})
export class ScheduleSettingsComponent implements OnInit {

  /** Emits schedule data to parent component whenever it changes */
  @Output() scheduleChange = new EventEmitter<any[]>();

  /** Reference to the Angular Material calendar to manually trigger refresh */
  @ViewChild(MatCalendar) calendar!: MatCalendar<Date>;

  /** Set of all dates included in the current schedule (used for fast lookup) */
  bookedDates: Set<string> = new Set();

  /** Dates marked as 'Available' (green in calendar) */
  bookedAvailableDates: Set<string> = new Set();

  /** Dates marked as 'Busy' (red in calendar) */
  bookedBusyDates: Set<string> = new Set();

  /** List of possible status options */
  statusOptions: string[] = ['Available', 'Busy'];

  /** Weekday configuration array (each represents a day of the week) */
  weekDays: WeekDay[] = [
    { name: 'Sunday', value: 0, selected: false, from: '09:00', to: '20:00', status: 'Available' },
    { name: 'Monday', value: 1, selected: false, from: '09:00', to: '20:00', status: 'Available' },
    { name: 'Tuesday', value: 2, selected: false, from: '09:00', to: '20:00', status: 'Available' },
    { name: 'Wednesday', value: 3, selected: false, from: '09:00', to: '20:00', status: 'Available' },
    { name: 'Thursday', value: 4, selected: false, from: '09:00', to: '20:00', status: 'Available' },
    { name: 'Friday', value: 5, selected: false, from: '09:00', to: '20:00', status: 'Available' },
    { name: 'Saturday', value: 6, selected: false, from: '09:00', to: '20:00', status: 'Available' }
  ];

  constructor(
    private settingService: SettingsService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder
  ) {}

  /**
   * Angular lifecycle hook
   * Loads saved schedule data on initialization.
   */
  ngOnInit(): void {
    this.loadBookedDates();
  }

  /**
   * Converts a Date object into a string in YYYY-MM-DD format.
   */
  private dateToString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Forces the calendar to re-render (important after status or selection changes).
   */
  private updateCalendar(): void {
    if (this.calendar) {
      this.calendar.updateTodaysDate(); // triggers internal refresh
    }
    this.cdr.detectChanges(); // ensures Angular view updates immediately
  }

  /**
   * Converts 24-hour format (HH:mm) → 12-hour AM/PM format.
   * Example: "18:00" → "06:00 PM"
   */
  private formatTimeToAmPm(time: string): string {
    if (!time) return '';
    const [hourStr, minuteStr] = time.split(':');
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour.toString().padStart(2, '0')}:${minuteStr} ${ampm}`;
  }

  /**
   * Converts AM/PM formatted time → 24-hour format.
   * Example: "06:00 PM" → "18:00"
   */
  private parseAmPmTo24(time: string): string {
    if (!time) return '';
    const match = time.match(/(\d+):(\d+)\s?(AM|PM)/i);
    if (!match) return time;
    let [_, hours, minutes, ampm] = match;
    let h = parseInt(hours, 10);
    if (ampm.toUpperCase() === 'PM' && h < 12) h += 12;
    if (ampm.toUpperCase() === 'AM' && h === 12) h = 0;
    return `${h.toString().padStart(2, '0')}:${minutes}`;
  }

  /**
   * Loads schedule data from backend service.
   * Populates the weekday list and rebuilds the colored date sets.
   */
  loadBookedDates(): void {
    this.settingService.getOrderBookingSchedule().subscribe({
      next: (res: any) => {
        // Clear all existing data before reloading
        this.bookedDates.clear();
        this.bookedAvailableDates.clear();
        this.bookedBusyDates.clear();

        // Handle both array and object formats
        const weeklyArray = Array.isArray(res)
          ? res
          : Array.isArray(res?.weeklyAvailability)
          ? res.weeklyAvailability
          : [];

        // Map backend data to local weekDays structure
        this.weekDays.forEach(day => {
          const found = weeklyArray.find((d: any) => d.value === day.value);
          if (found) {
            day.selected = true;
            day.from = this.parseAmPmTo24(found.from);
            day.to = this.parseAmPmTo24(found.to);
            day.status = found.status || 'Available';
            this.addAllDatesForWeekday(day);
          } else {
            day.selected = false;
          }
        });

        // Refresh the calendar after data load
        setTimeout(() => this.updateCalendar(), 0);

        // Notify parent component about the loaded schedule
        this.emitSchedule();
      },
      error: (err) => {
        console.error('Error loading bookings:', err);
        this.bookedDates.clear();
      }
    });
  }

  /**
   * Returns CSS class for a given calendar date based on its status.
   * - 'busy-day' → red background
   * - 'available-day' → green background
   */
  dateClass = (date: Date): string => {
    const dateStr = this.dateToString(date);
    if (this.bookedBusyDates.has(dateStr)) return 'busy-day';
    if (this.bookedAvailableDates.has(dateStr)) return 'available-day';
    return '';
  };

  /**
   * Triggered when a weekday checkbox, time, or status changes.
   * Rebuilds the date sets and refreshes the calendar.
   */
  onWeekDayChange(weekDay: WeekDay): void {
    // Remove previously assigned dates for this weekday
    this.removeAllDatesForWeekday(weekDay);

    // If the day is still selected, re-add it with new settings
    if (weekDay.selected) {
      this.addAllDatesForWeekday(weekDay);
    }

    // Small delay to ensure DOM update timing
    setTimeout(() => this.updateCalendar(), 0);

    // Emit updated schedule to parent
    this.emitSchedule();
  }

  /**
   * Adds all future dates (1 year ahead) for a specific weekday.
   * Each date is added to either the 'available' or 'busy' set based on the status.
   */
  addAllDatesForWeekday(weekDay: WeekDay): void {
    const today = new Date();
    const endDate = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
    const current = new Date(today);

    while (current <= endDate) {
      if (current.getDay() === weekDay.value) {
        const dateStr = this.dateToString(current);
        // Add to general list
        this.bookedDates.add(dateStr);

        // Categorize by status
        if (weekDay.status === 'Busy') {
          this.bookedBusyDates.add(dateStr);
          this.bookedAvailableDates.delete(dateStr);
        } else {
          this.bookedAvailableDates.add(dateStr);
          this.bookedBusyDates.delete(dateStr);
        }
      }
      current.setDate(current.getDate() + 1);
    }
  }

  /**
   * Removes all dates associated with a particular weekday from every tracking set.
   */
  removeAllDatesForWeekday(weekDay: WeekDay): void {
    const removeList: string[] = [];
    this.bookedDates.forEach(dateStr => {
      const [y, m, d] = dateStr.split('-').map(Number);
      const date = new Date(y, m - 1, d);
      if (date.getDay() === weekDay.value) {
        removeList.push(dateStr);
        this.bookedAvailableDates.delete(dateStr);
        this.bookedBusyDates.delete(dateStr);
      }
    });
    removeList.forEach(dateStr => this.bookedDates.delete(dateStr));
  }

  /**
   * Builds the final weekly availability array and emits it to the parent component.
   */
  private emitSchedule(): void {
    const weeklyAvailability = this.weekDays
      .filter(day => day.selected)
      .map(day => ({
        day: day.name,
        value: day.value,
        from: this.formatTimeToAmPm(day.from),
        to: this.formatTimeToAmPm(day.to),
        status: day.status
      }));

    this.scheduleChange.emit(weeklyAvailability);
  }
}
